import { useNavigate, useParams } from 'react-router';
import { useQuery } from '@apollo/client';
import { TableColumnsType } from 'antd';

import { tableSortingMethods } from '../../../utils';

import {
  FeatureName,
  LOCATION_LEVEL_SECTIONS,
  locationLevelSections,
} from '../constants';

import { Location } from '../../../interfaces/RiskHub';

import { useLocationName } from '../../../hooks';

import { Footer, TitleBar } from '../../../components';
import FinancialData from '../../../components/common/FinancialData';
import TitleUpdater from '../../../components/common/TitleUpdater';
import QueryErrorBoundary from '../../../components/error-handling';
import TableV3 from '../../../components/generic/Table/TableV3';

import ITSection from '../components/ITSection';
import { FactorsRenderer } from '../components';

import { GET_LOCATION_IT_APPLICATION_DASHBOARD } from './queries';
import {
  ItApplicationCard,
  ItApplications,
  ITApplicationsDashboard,
  QualitativeFactorsRELC,
} from '../enterprise-applications';
import { useState } from 'react';
import { Tier } from '../../dataInputs/tiers/types';
import { TierBadge } from '../../dataInputs/tiers/components';
import { useHandleToggleFeature } from '../../../hooks/cyber/useHandleToggleFeature';
import ToggleFeatureModel, {
  SelectedFeature,
} from '../components/ToggleFeatureModel';

export type FetchedLocation = {
  findLocationByUuid: Location;
};

const LocationLevelLanding = () => {
  const { locationId } = useParams();
  const { locationName, locationNameError } = useLocationName(locationId);

  const [isToggleFeatureModalOpen, setIsToggleFeatureModalOpen] =
    useState(false);
  const [selectedFeature, setSelectedFeature] =
    useState<SelectedFeature | null>(null);

  const navigate = useNavigate();

  const [stats, setStats] = useState<{
    [k in LOCATION_LEVEL_SECTIONS]?: ItApplicationCard;
  }>({
    [LOCATION_LEVEL_SECTIONS.APPLICATION_REVENUE_MAPPING]: {
      count: 0,
      isEnabled: false,
    },
    [LOCATION_LEVEL_SECTIONS.PROCESS_AREA_AND_PROCESS_TAGGING]: {
      count: 0,
      isEnabled: false,
    },
    [LOCATION_LEVEL_SECTIONS.BUSINESS_FUNCTION_TAGGING]: {
      count: 0,
      isEnabled: false,
    },
    [LOCATION_LEVEL_SECTIONS.QUALITATIVE_FACTORS]: {
      count: 0,
      isEnabled: false,
    },
  });

  const [applications, setApplications] = useState<ItApplications[]>([]);

  const { loading, error, refetch } = useQuery<ITApplicationsDashboard>(
    GET_LOCATION_IT_APPLICATION_DASHBOARD,
    {
      variables: {
        locationUUID: locationId,
      },
      onCompleted: ({
        getItApplicationsDashboard: {
          cards: {
            applicationsMapped,
            processAreaMapped,
            businessFunctionsMapped,
            qualitativeFactorsMapped,
          },
          applications,
        },
      }) => {
        setStats({
          [LOCATION_LEVEL_SECTIONS.APPLICATION_REVENUE_MAPPING]:
            applicationsMapped,
          [LOCATION_LEVEL_SECTIONS.PROCESS_AREA_AND_PROCESS_TAGGING]:
            processAreaMapped || { count: 0, isEnabled: false },
          [LOCATION_LEVEL_SECTIONS.BUSINESS_FUNCTION_TAGGING]:
            businessFunctionsMapped,
          [LOCATION_LEVEL_SECTIONS.QUALITATIVE_FACTORS]:
            qualitativeFactorsMapped,
        });

        setApplications(applications);
      },
    },
  );

  const columns: TableColumnsType<ItApplications> = [
    {
      title: 'Applications',
      width: 120,
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      sortOrder: 'ascend',
      sorter: (a, b) => tableSortingMethods.string(a.name, b.name),
      render: (text: string) => (
        <span className="custom-table--column ml-10">{text}</span>
      ),
    },
    {
      title: 'Tier',
      dataIndex: 'tier',
      key: 'tier',
      align: 'center',
      width: 60,
      sorter: (a: ItApplications, b: ItApplications) =>
        tableSortingMethods.string(a.tier?.name, b.tier?.name),
      render: (tier: Tier) =>
        tier ? (
          <TierBadge
            color={tier.color}
            name={tier.name}
            className="custom-table--column"
          />
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
    {
      title: (
        <span>
          Annual Revenue <br />
          Supported
        </span>
      ),
      width: 80,
      dataIndex: 'revenue',
      key: 'revenue',
      align: 'right',
      sorter: (a, b) => tableSortingMethods.number(a.revenue, b.revenue),
      render: (text: number) => (
        <span className="custom-table--column">
          <FinancialData text={text} formatType="decimal" />
        </span>
      ),
    },
    {
      title: (
        <span>
          Process Areas <br />
          Supported
        </span>
      ),
      width: 120,
      dataIndex: 'processAreas',
      key: 'processAreas',
      align: 'center',
      sorter: (a, b) =>
        tableSortingMethods.number(a.processAreas, b.processAreas),
      render: (text: number) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
    {
      title: (
        <span>
          Processes <br />
          Supported
        </span>
      ),
      width: 120,
      dataIndex: 'processes',
      key: 'processes',
      align: 'center',
      sorter: (a, b) => tableSortingMethods.number(a.processes, b.processes),
      render: (text: number) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
    {
      title: (
        <span>
          Business Functions <br />
          Supported
        </span>
      ),
      width: 120,
      dataIndex: 'businessFunctions',
      key: 'businessFunctions',
      align: 'center',
      sorter: (a, b) =>
        tableSortingMethods.number(a.businessFunctions, b.businessFunctions),
      render: (text: number) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
    {
      title: (
        <span>
          Qualitative Factors <br />
          Supported
        </span>
      ),
      width: 120,
      dataIndex: 'qualitativeFactors',
      key: 'qualitativeFactors',
      align: 'left',
      ellipsis: true,
      render: (factors: QualitativeFactorsRELC) => {
        return (
          <div className="custom-table--column space-x-1">
            <FactorsRenderer factors={factors} />
          </div>
        );
      },
    },
  ];

  const extendOptions = (
    <div className="w-full text-sm ml-3 lg:ml-0 lg:text-base font-bold text-primary-200 min-w-max">
      {`${applications.length} Applications`}
    </div>
  );

  const { handleToggleFeature, toggleFeatureLoading } = useHandleToggleFeature({
    refetch,
    setStats,
  });

  const onCancel = () => {
    setIsToggleFeatureModalOpen(false);
    setSelectedFeature(null);
  };

  const onConfirm = (selectedFeature: SelectedFeature) => {
    setSelectedFeature(selectedFeature);
    setIsToggleFeatureModalOpen(true);
  };

  const afterConfirm = async () => {
    await handleToggleFeature(
      selectedFeature?.flag ?? '',
      selectedFeature?.feature ?? '',
      selectedFeature?.isEnabled ?? false,
      locationId,
    );
    onCancel();
  };

  const shouldCallAfterConfirm =
    (selectedFeature?.numberOfApplications ?? 0) < 1;

  return (
    <QueryErrorBoundary error={locationNameError}>
      <TitleUpdater title={`Location Level/${locationName} `} />
      <TitleBar
        title="Location Application Dependency Mapping"
        subtitle={`${locationName} Overview`}
        brief={`Visit any section below to add or edit information for the IT applications that support the ${locationName} location.`}
      />
      <QueryErrorBoundary error={error}>
        <div className="grid--4col gap-4">
          {locationLevelSections.map((section) => {
            const sectionStat = stats?.[section.title];
            const isEnabled = sectionStat?.isEnabled ?? false;
            const count = sectionStat?.count ?? 0;

            const onAction = !isEnabled
              ? () =>
                  handleToggleFeature(
                    FeatureName[section.title],
                    section.title,
                    isEnabled,
                    locationId,
                  )
              : () =>
                  onConfirm({
                    flag: FeatureName[section.title],
                    feature: section.title,
                    isEnabled: isEnabled,
                    numberOfApplications: count,
                    reviewLink: locationId
                      ? section.reviewLink(locationId)
                      : '#',
                  });

            return (
              <ITSection
                key={section.title}
                title={section.title}
                description={section.description}
                overview={count}
                placeholderText={
                  section.title ===
                  LOCATION_LEVEL_SECTIONS.APPLICATION_REVENUE_MAPPING
                    ? 'applications support revenue'
                    : undefined
                }
                reviewLink={locationId ? section.reviewLink(locationId) : '#'}
                onAction={onAction}
                isEnabled={!loading && isEnabled}
                loading={loading || toggleFeatureLoading}
              />
            );
          })}
        </div>
        <div className="mt-3">
          <TableV3
            columns={columns}
            data={applications}
            loading={loading}
            useBorderedSection
            extendOptions={extendOptions}
            tableSize="small"
          />
        </div>
      </QueryErrorBoundary>
      <div className="flex items-center justify-between">
        <Footer backPath={`/cyber/location-level/${locationId}/options`} />
      </div>
      <ToggleFeatureModel
        selectedFeature={selectedFeature}
        isToggleFeatureModalOpen={isToggleFeatureModalOpen}
        onCancel={onCancel}
        onConfirm={
          shouldCallAfterConfirm
            ? afterConfirm
            : () => navigate(selectedFeature?.reviewLink ?? '')
        }
      />
    </QueryErrorBoundary>
  );
};

export default LocationLevelLanding;
